'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import axios from 'axios';

interface UserProfile {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  ispro: boolean;
  isadmin: boolean;
  istrial: boolean;
  issubadmin: boolean;
  trialeligible: boolean;
  subscription: [];
  organization?: {
    name?: string;
    title?: string;
    size?: {
      id?: number;
      name?: string;
    };
    type?: {
      name?: string;
    };
    orgsizeid?: number;
    orgtypeid?: number;
  };
  profilepicture: {
    type?: string;
    small?: string;
    large?: string;
    medium?: string;
    xlarge?: string;
    text?: string;
  };
}

declare const window: Window & {
  sug_memberinfo: {
    USERTYPE?: number;
    SUBSCRIPTIONS?: [];
    SIGNUPS?: object;
    MEMBERSINCE?: number;
    MEMBERID?: number;
    ISPRO?: number;
    ELIGIBLEFORTRIAL?: boolean;
    BILLING?: string;
    ISTRIAL?: boolean | number;
  };
};

export interface UserRole {
  parent: {
    id: number;
    firstname: string;
    lastname: string;
  };
  role: {
    id: number;
    name: string;
  };
}

interface UserProfileContextType {
  userProfile: UserProfile | null;
  userRoles: UserRole[];
  isLoading: boolean;
  fetchUserProfile: () => Promise<void>;
  handleLogout: () => void;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

export const UserProfileProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();

  const fetchUserRoles = async () => {
    try {
      const cachedRoles = localStorage.getItem('userRoles');
      const cacheTimestamp = localStorage.getItem('userRolesCacheTime');
      const now = new Date().getTime();

      if (cachedRoles && cacheTimestamp) {
        const cacheAge = now - parseInt(cacheTimestamp);
        if (cacheAge < 24 * 60 * 60 * 1000) {
          setUserRoles(JSON.parse(cachedRoles));
          return;
        }
      }

      const response = await axios.get('/api/auth/role');
      if (response.data.success && response.data.data) {
        setUserRoles(response.data.data);
        localStorage.setItem('userRoles', JSON.stringify(response.data.data));
        localStorage.setItem('userRolesCacheTime', now.toString());
      } else {
        setUserRoles([]);
        localStorage.setItem('userRoles', JSON.stringify([]));
        localStorage.setItem('userRolesCacheTime', now.toString());
      }
    } catch {
      setUserRoles([]);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const validateResponse = await axios.get('/api/auth/validate');
      if (validateResponse?.data?.isValid) {
        // Only fetch profile if validation is valid and profile is not already loaded
        if (!userProfile) {
          const response = await axios.get('/api/profile', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          });
          if (response.data.success) {
            setUserProfile(response.data.data);
            await fetchUserRoles();
          } else {
            setUserProfile(null);
          }
        }
      } else {
        setUserProfile(null);
      }
    } catch {
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userRoles');
    localStorage.removeItem('userRolesCacheTime');
    setUserProfile(null);
    setUserRoles([]);
  };

  // Only fetch on pathname changes
  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (userProfile) {
      window.sug_memberinfo = {
        MEMBERID: userProfile?.id,
        ELIGIBLEFORTRIAL: userProfile?.trialeligible,
        SUBSCRIPTIONS: userProfile?.subscription,
        ISPRO: userProfile?.ispro ? 1 : 0,
        ISTRIAL: userProfile?.istrial,
      };
    } else {
      window.sug_memberinfo = {
        USERTYPE: 0,
        SUBSCRIPTIONS: [],
        SIGNUPS: {},
        MEMBERSINCE: 0,
        MEMBERID: 0,
        ISPRO: 0,
        ELIGIBLEFORTRIAL: false,
        BILLING: '',
        ISTRIAL: 0,
      };
    }
  }, [userProfile]); // empty deps → only once on mount

  return (
    <UserProfileContext.Provider
      value={{
        userProfile,
        userRoles,
        isLoading,
        fetchUserProfile,
        handleLogout,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
